import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '@/api/services';
import { queryClient } from '@/api/query-client';
import { useNavigationStore } from '@/stores/navigation-store';
import { useSessionStore } from '@/stores/session-store';

const CONTEXT_CHANNEL = 'invoice-context';

interface ContextChangedMessage {
  type: 'CONTEXT_CHANGED';
  version: string;
}

function openContextChannel() {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CONTEXT_CHANNEL);
}

function publishContextChanged(version: string) {
  const channel = openContextChannel();
  channel?.postMessage({ type: 'CONTEXT_CHANGED', version } satisfies ContextChangedMessage);
  channel?.close();
}

export function useOrganizationContextSwitch() {
  const applyContextSession = useSessionStore((state) => state.applyContextSession);
  const setMe = useSessionStore((state) => state.setMe);
  const setContextSwitching = useSessionStore((state) => state.setContextSwitching);
  const closeAll = useNavigationStore((state) => state.closeAll);

  return useMutation({
    mutationFn: async ({ organizationId, sellerId = null }: { organizationId: string; sellerId?: string | null }) => {
      setContextSwitching(true);
      await queryClient.cancelQueries();
      const result = await api.switchContext({ organizationId, sellerId });
      queryClient.clear();
      let nextMe = applyContextSession(result.accessToken, result.context, result.permissions);
      if (!nextMe) {
        useSessionStore.getState().setAccessToken(result.accessToken);
        nextMe = await api.me();
        setMe(nextMe);
      }
      closeAll();
      publishContextChanged(result.context.contextVersion);
      return nextMe;
    },
    onSettled: () => setContextSwitching(false),
  });
}

export function useOrganizationContextBroadcastSync() {
  useEffect(() => {
    const channel = openContextChannel();
    if (!channel) return undefined;
    channel.onmessage = (event: MessageEvent<ContextChangedMessage>) => {
      if (event.data?.type !== 'CONTEXT_CHANGED') return;
      if (useSessionStore.getState().me?.context.contextVersion === event.data.version) return;
      void (async () => {
        const session = useSessionStore.getState();
        session.setContextSwitching(true);
        session.setAccessToken(null);
        session.setStatus('refreshing');
        try {
          await queryClient.cancelQueries();
          const refreshed = await api.refresh();
          session.establishSession(refreshed.accessToken);
          queryClient.clear();
          const nextMe = await api.me();
          session.setMe(nextMe);
          useNavigationStore.getState().closeAll();
        } catch {
          session.clearSession('expired');
        } finally {
          useSessionStore.getState().setContextSwitching(false);
        }
      })();
    };
    return () => channel.close();
  }, []);
}
